import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';

const emptyByType = {
  merch: {
    type: 'merch', name: '', description: '', imageUrl: '', active: true,
    variants: [{ sku: '', size: '', color: '', stock: 0, stripePriceId: '' }],
  },
  ticket: {
    type: 'ticket', name: '', description: '', imageUrl: '', active: true,
    eventId: '', priceCents: 0, stripePriceId: '', capacity: 100, sold: 0,
  },
  dues: {
    type: 'dues', name: '', description: '', imageUrl: '', active: true,
    priceCents: 0, stripePriceId: '', intervalMonths: 12,
  },
};

const typeChip = { merch: 'chip-brand', ticket: 'chip-success', dues: 'chip-warn' };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyByType.merch);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await Admin.products.list();
    setProducts(r.products);
  }
  useEffect(() => { refresh(); }, []);

  function openCreate(type = 'merch') { setForm(emptyByType[type]); setModal('create'); }
  function openEdit(p) { setForm(structuredClone(p)); setModal({ id: p.id }); }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.type === 'ticket') {
        payload.priceCents = Number(payload.priceCents); payload.capacity = Number(payload.capacity); payload.sold = Number(payload.sold || 0);
      }
      if (payload.type === 'dues') {
        payload.priceCents = Number(payload.priceCents); payload.intervalMonths = Number(payload.intervalMonths);
      }
      if (payload.type === 'merch') {
        payload.variants = payload.variants.map((v) => ({ ...v, stock: Number(v.stock) }));
      }
      if (modal === 'create') await Admin.products.create(payload);
      else await Admin.products.update(modal.id, payload);
      setModal(null);
      await refresh();
    } finally { setBusy(false); }
  }

  async function remove(id) { await Admin.products.remove(id); await refresh(); }
  function switchType(t) { setForm(emptyByType[t]); }

  function updateVariant(i, k, v) {
    setForm((f) => {
      const variants = [...f.variants];
      variants[i] = { ...variants[i], [k]: v };
      return { ...f, variants };
    });
  }
  function addVariant() {
    setForm((f) => ({ ...f, variants: [...f.variants, { sku: '', size: '', color: '', stock: 0, stripePriceId: '' }] }));
  }
  function removeVariant(i) {
    setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));
  }

  return (
    <div>
      <PageHeader
        eyebrow="Shop"
        title="Products"
        description="Kit, match tickets, and seasonal dues."
        action={
          <div className="flex gap-2">
            <button onClick={() => openCreate('merch')} className="btn-outline btn-sm">+ Merch</button>
            <button onClick={() => openCreate('ticket')} className="btn-outline btn-sm">+ Ticket</button>
            <button onClick={() => openCreate('dues')} className="btn-primary btn-sm">+ Dues</button>
          </div>
        }
      />

      {products.length === 0 ? (
        <EmptyState onAction={() => openCreate('merch')} label="Add a product" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <article key={p.id} className={`card flex flex-col ${!p.active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className={typeChip[p.type] || 'chip-neutral'}>{p.type}</span>
                {p.active ? <span className="chip-success chip-dot">Live</span> : <span className="chip-neutral">Draft</span>}
              </div>
              <h3 className="font-display text-xl">{p.name}</h3>
              <div className="text-xs text-ink-2 mt-2">
                {p.type === 'merch' && <span>{p.variants?.length || 0} variant{p.variants?.length === 1 ? '' : 's'}</span>}
                {p.type === 'ticket' && <span>${(p.priceCents / 100).toFixed(2)} · {p.sold || 0}/{p.capacity} sold</span>}
                {p.type === 'dues' && <span>${(p.priceCents / 100).toFixed(2)} / {p.intervalMonths}mo</span>}
              </div>
              <div className="mt-auto pt-5 flex gap-2">
                <button className="btn-outline btn-xs" onClick={() => openEdit(p)}>Edit</button>
                <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(p)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New product' : 'Edit'}
        title={form.name || `New ${form.type}`}
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('product-form')} className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        }
      >
        <form id="product-form" onSubmit={save} className="space-y-5">
          {modal === 'create' && (
            <div className="flex gap-1 p-1 bg-line-soft rounded-lg w-fit">
              {['merch', 'ticket', 'dues'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => switchType(t)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    form.type === t ? 'bg-paper text-ink shadow-card' : 'text-ink-2'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          <FormField label="Name" required>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          <FormField label="Image URL" hint="Full https:// URL">
            <input className="input" value={form.imageUrl || ''} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
          </FormField>

          {form.type === 'merch' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="label mb-0">Variants</span>
                <button type="button" className="btn-ghost btn-xs" onClick={addVariant}>+ Add</button>
              </div>
              <div className="space-y-2">
                {form.variants.map((v, i) => (
                  <div key={i} className="border border-line rounded-md p-3 grid grid-cols-2 gap-2">
                    <input className="input" placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, 'sku', e.target.value)} />
                    <input className="input" placeholder="Stripe price ID" value={v.stripePriceId} onChange={(e) => updateVariant(i, 'stripePriceId', e.target.value)} />
                    <input className="input" placeholder="Size" value={v.size} onChange={(e) => updateVariant(i, 'size', e.target.value)} />
                    <input className="input" placeholder="Color" value={v.color} onChange={(e) => updateVariant(i, 'color', e.target.value)} />
                    <input type="number" className="input col-span-2" placeholder="Stock" value={v.stock} onChange={(e) => updateVariant(i, 'stock', e.target.value)} />
                    {form.variants.length > 1 && (
                      <button type="button" className="col-span-2 text-[11px] text-danger text-left" onClick={() => removeVariant(i)}>
                        Remove variant
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.type === 'ticket' && (
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Event ID" hint="Firestore event id" required>
                <input className="input" value={form.eventId || ''} onChange={(e) => setForm({ ...form, eventId: e.target.value })} required />
              </FormField>
              <FormField label="Price (cents)" required>
                <input type="number" className="input" value={form.priceCents} onChange={(e) => setForm({ ...form, priceCents: e.target.value })} required />
              </FormField>
              <FormField label="Stripe price ID" required>
                <input className="input" value={form.stripePriceId} onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })} required />
              </FormField>
              <FormField label="Capacity" required>
                <input type="number" className="input" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} required />
              </FormField>
            </div>
          )}

          {form.type === 'dues' && (
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Price (cents)" required>
                <input type="number" className="input" value={form.priceCents} onChange={(e) => setForm({ ...form, priceCents: e.target.value })} required />
              </FormField>
              <FormField label="Interval (months)" required>
                <input type="number" min={1} max={24} className="input" value={form.intervalMonths} onChange={(e) => setForm({ ...form, intervalMonths: e.target.value })} required />
              </FormField>
              <FormField label="Stripe price ID" required>
                <input className="input" value={form.stripePriceId} onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })} required />
              </FormField>
            </div>
          )}

          <Switch checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="Live in the shop" />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete ${confirm?.name}?`}
        message="This removes the product. Paid orders are unaffected."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
